import { PermissionsCacheEnvelope,PermissionSetDtoV2 } from './permissionsV2.types'

const SALT_KEY   = 'cop:perm-salt'
const HKDF_INFO  = new TextEncoder().encode('cop-permissions-v2')
const TTL_MS     = 5 * 60 * 1000  // 5 minutes

// ── helpers ──────────────────────────────────────────────────────────────────

const b64encode = (buf: ArrayBuffer): string =>
    btoa(String.fromCharCode(...new Uint8Array(buf)))

const b64decode = (s: string): Uint8Array =>
    Uint8Array.from(atob(s), c => c.charCodeAt(0))

// ── salt management ───────────────────────────────────────────────────────────

export const getOrCreateSalt = (): Uint8Array => {
    const stored = sessionStorage.getItem(SALT_KEY)
    if (stored) return b64decode(stored)
    const salt = crypto.getRandomValues(new Uint8Array(16))
    sessionStorage.setItem(SALT_KEY, b64encode(salt))
    return salt
}

export const clearSalt = (): void => sessionStorage.removeItem(SALT_KEY)

// ── key derivation ────────────────────────────────────────────────────────────

export const deriveKey = async (authToken: string, salt: Uint8Array): Promise<CryptoKey> => {
    const ikm = await crypto.subtle.importKey(
        'raw',
        new TextEncoder().encode(authToken),
        { name: 'HKDF' },
        false,
        ['deriveKey'],
    )
    return crypto.subtle.deriveKey(
        { name: 'HKDF', hash: 'SHA-256', salt, info: HKDF_INFO },
        ikm,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt'],
    )
}

// ── encrypt ───────────────────────────────────────────────────────────────────

export const encryptPermissions = async (
    data: PermissionSetDtoV2,
    key: CryptoKey,
): Promise<string> => {
    const iv         = crypto.getRandomValues(new Uint8Array(12))
    const plaintext  = new TextEncoder().encode(JSON.stringify(data))
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext)
    const envelope: PermissionsCacheEnvelope = {
        iv: b64encode(iv),
        ct: b64encode(ciphertext),
        at: Date.now(),
    }
    return JSON.stringify(envelope)
}

// ── decrypt ───────────────────────────────────────────────────────────────────

export const decryptPermissions = async (
    stored: string,
    key: CryptoKey,
): Promise<PermissionSetDtoV2 | null> => {
    try {
        const envelope: PermissionsCacheEnvelope = JSON.parse(stored)
        if (Date.now() - envelope.at > TTL_MS) return null   // expired — discard before decrypt
        const iv         = b64decode(envelope.iv)
        const ciphertext = b64decode(envelope.ct)
        const plaintext  = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext)
        return JSON.parse(new TextDecoder().decode(plaintext)) as PermissionSetDtoV2
    } catch {
        return null  // tampered, corrupted, or wrong key → treat as cache miss
    }
}

// ── TTL check (sync, no decryption) ──────────────────────────────────────────

export const isCacheEnvelopeValid = (stored: string | null): boolean => {
    if (!stored) return false
    try {
        const envelope: PermissionsCacheEnvelope = JSON.parse(stored)
        return Date.now() - envelope.at <= TTL_MS
    } catch {
        return false
    }
}
